import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PaginatedResponse } from '@supernova/shared';

/**
 * Warehouses a vendor can be linked to: this tenant's own warehouses
 * (`source: 'location'`) plus other tenants' private warehouses super admin
 * mapped to `locationId` (`source: 'private'`, with `ownerTenantName`).
 */
export function useWarehouses(locationId?: string) {
  return useQuery({
    queryKey: ['warehouses', locationId ?? null],
    queryFn: () => api.get<{ data: any[] }>('/warehouses', locationId ? { locationId } : undefined),
  });
}

export function useWarehouseOrders(params: Record<string, string | number | boolean | undefined> = {}) {
  return useQuery({
    queryKey: ['warehouse-orders', params],
    queryFn: () => api.get<PaginatedResponse<any>>('/purchase-orders/warehouse-orders', params),
  });
}

export function useCheckWarehouseAvailability() {
  return useMutation({
    mutationFn: (data: {
      payeeId: string;
      locationId?: string;
      items: { productId: string; unitOfMeasure: string; quantity: number }[];
    }) => api.post<{ data: { available: boolean; items: any[] } }>('/purchase-orders/check-availability', data),
  });
}

export function useFulfillWarehouseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/purchase-orders/${id}/fulfill`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export function useWarehouseEditSaleOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: Array<{ id: string; quantity: number }> }) =>
      api.patch<{ data: any }>(`/purchase-orders/${id}/warehouse-edit`, { items }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['warehouse-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-order', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['po-edits', variables.id] });
    },
  });
}

export function usePurchaseOrderEdits(poId: string) {
  return useQuery({
    queryKey: ['po-edits', poId],
    queryFn: () => api.get<{ data: any[] }>(`/purchase-orders/${poId}/edits`),
    enabled: !!poId,
  });
}
